import {
  NamedBindings, SourceEventForList, CurrentContextService, CaseListSnapshot,
  CachedCaseListElement, UserServiceInterface, CoreConfigurationInterface,
  parseElementWithZod,
  ZsourceEventsSchema,
  ZodSchemas,
  ZsourceEvents,
  ZcaseListSnapshotSchema,
  ZcaseListSnapshot
} from "@dandelion/core";
import { RESTStorageForEvents as CoreRESTStorageForEvents } from "@dandelion/core/storages";
import { inject, injectable } from "inversify";
import axios from "axios";
import { toast } from "vue3-toastify";
import { NamedServices } from "../enum/NamedServices";
import { YearService } from "../services/YearService";

@injectable()
export class RESTStorageForEvents extends CoreRESTStorageForEvents {
  protected BASE_URL_GET = import.meta.env.VITE_APP_API_BASE_URL + 'Events/';
  protected BASE_URL_POST = import.meta.env.VITE_APP_API_BASE_URL + 'Events/event/';
  protected cache: SourceEventForList[] | null = null;
  protected cacheExpiring: Date = new Date();
  protected EventsForCentreCode: Map<string, SourceEventForList[]> = new Map();
  protected eventCounters: Map<string, number> = new Map();

  constructor(
    @inject(NamedBindings.UserService) protected userService: UserServiceInterface,
    @inject(NamedBindings.CurrentContextService) protected currentContextService: CurrentContextService,
    @inject(NamedBindings.CoreConfiguration) protected coreConfiguration: CoreConfigurationInterface,
    @inject(NamedServices.YearService) protected yearService: YearService,
  ) {
    super(userService, currentContextService, coreConfiguration)
    axios.interceptors.request.use((config: any) => {
      config.headers = this.userService.getHeaders();
      return config;
    });
  }

  async getLastSnapshotAndEvents(): Promise<{ events: SourceEventForList[]; snapshot: CaseListSnapshot }> {
    const centreCode = this.yearService.getCentreCodeWithSelectedYear().value;
    const previousYearCentreCode = this.yearService.getCentreCodeWithPreviousYear().value;
    const caseType = this.currentContextService.getCaseConfiguration().caseName;
    const urls = [`${this.BASE_URL_GET}GetSnapshotAndEvents/${centreCode}/${caseType}/`];

    if (previousYearCentreCode) {
      urls.push(`${this.BASE_URL_GET}GetSnapshotAndEvents/${previousYearCentreCode}/${caseType}/`);
    }

    let combinedEvents: SourceEventForList[] = [];
    let snapshotsArray: CaseListSnapshot[] = [];

    try {
      const responses = await Promise.all(urls.map(url => axios.get(url)));
      for (const response of responses) {
        const { events, snapshot } = response.data;
        const parsedEvents = parseElementWithZod(
          ZsourceEventsSchema,
          ZodSchemas.SOURCE_EVENT,
          events.map((event: any) => JSON.parse(event.data))
        ) as ZsourceEvents;

        combinedEvents.push(...parsedEvents);

        // Gestione eventi per centro
        const responseUrl = response.config.url;
        if (responseUrl) {
          const urlPathParts = responseUrl.split('/');
          const extractedCentreCode = urlPathParts[urlPathParts.indexOf('GetSnapshotAndEvents') + 1];

          let existingEvents = this.EventsForCentreCode.get(extractedCentreCode);
          if (!existingEvents || existingEvents.length === 0) {
            await this.loadAllEventsForCentreCode(extractedCentreCode).then(allEvents => {
              this.EventsForCentreCode.set(extractedCentreCode, allEvents);
              existingEvents = allEvents;
            });
          }

          const combinedEventsSet = new Map<string, any>();
          existingEvents!.forEach(event => combinedEventsSet.set(event.id, event));
          parsedEvents.forEach((event: any) => combinedEventsSet.set(event.id, event));

          const uniqueCombinedEvents = Array.from(combinedEventsSet.values());
          this.EventsForCentreCode.set(extractedCentreCode, uniqueCombinedEvents);

          const currentCount = this.eventCounters.get(extractedCentreCode) || 0;
          if (parsedEvents.length > currentCount) {
            this.eventCounters.set(extractedCentreCode, parsedEvents.length);
          }
        }

        // Normalizzazione snapshot
        const normalizedSnapshot = new CaseListSnapshot(
          snapshot.cachedCaseList ?? [],
          snapshot.deletedCachedCaseList ?? [],
          snapshot.permanentlyDeletedCaseList ?? [],
          snapshot.date,
          snapshot.numberOfEvents
        );

        snapshotsArray.push(parseElementWithZod(ZcaseListSnapshotSchema, ZodSchemas.SNAPSHOT, normalizedSnapshot) as ZcaseListSnapshot);
      }

    } catch (error: any) {
      this.handleError(error);
    }

    const firstSnapshot = snapshotsArray.shift() ?? new CaseListSnapshot([], [], [], new Date(), 0);
    const latestSnapshot = snapshotsArray.reduce((result, snapshot) => {
      const lastSnapshotDate = snapshot.date > result.date ? snapshot.date : result.date;
      return new CaseListSnapshot(
        [...result.cachedCaseList, ...snapshot.cachedCaseList],
        [...result.deletedCachedCaseList, ...snapshot.deletedCachedCaseList],
        [...result.permanentlyDeletedCaseList, ...snapshot.permanentlyDeletedCaseList],
        lastSnapshotDate,
        result.numberOfEvents + snapshot.numberOfEvents
      );
    }, firstSnapshot);

    return {
      events: combinedEvents,
      snapshot: latestSnapshot,
    };
  }

  handleError(error: any) {
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      const responseHeadersHTML = `<details><summary>Response Headers</summary><dl>${formatHeadersAsHTML(
        error.response.headers
      )}</dl></details>`;
      const errorData = `<details><summary>Response Data</summary><dl>${formatData(
        error.response.data
      )}</dl></details>`;
      const errorString = error.message + '\n' + errorData + '\n' + responseHeadersHTML;
      console.log(errorString);
      setTimeout(
        () =>
          toast.error(errorString, {
            toastId:"restEventsError",
            dangerouslyHTMLString: true,
            closeOnClick: false,
            autoClose: false,
          }),
        0
      );
    } else if (error.request) {
      // The request was made but no response was received
      // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
      // http.ClientRequest in node.js
      if (error.request.response === '') {
        setTimeout(() => toast.error('Error: ' + error.message), 0);
      } else {
        setTimeout(() => toast.error(error.request), 0);
      }
    } else {
      // Something happened in setting up the request that triggered an Error
      setTimeout(() => toast.error('Error: ' + error.message), 0);
    }
    console.log(error.config);
  }

  async saveSnapshot(snapshot: CaseListSnapshot): Promise<void> {
    const centreCodeWithYear = this.yearService.getCentreCodeWithAdmissionYear().value;
    const caseType = this.currentContextService.getCaseConfiguration().caseName;

    // Extract the year from the centreCode
    const year = parseInt(centreCodeWithYear.split('_')[1], 10);
    if (isNaN(year)) {
      throw new Error('Invalid year in centreCode');
    }
    const referenceDate = new Date(year, 0, 1);
    const filteredList = snapshot.cachedCaseList.filter(
      (element: CachedCaseListElement) => {
        const elementDate = new Date(element.referenceDate);
        return elementDate.getFullYear() === referenceDate.getFullYear();
      }
    );
    const eventsForCentreCode = this.EventsForCentreCode.get(centreCodeWithYear) || [];
    const numberOfEvents = eventsForCentreCode.length;
    const eventCounterForCentreCode = this.eventCounters.get(centreCodeWithYear) || 0;
    if (eventCounterForCentreCode <= 10) {
      return;
    }

    const filteredDeletedList = snapshot.deletedCachedCaseList.filter(
      (element: CachedCaseListElement) => {
        const elementDate = new Date(element.referenceDate);
        return elementDate.getFullYear() === referenceDate.getFullYear();
      }
    );
    // There's no information to separate permanently deleted data by year, but also no need to do it
    const filteredPermanentlyDeletedList = snapshot.permanentlyDeletedCaseList;
    const filteredSnapshot = new CaseListSnapshot(filteredList, filteredDeletedList, filteredPermanentlyDeletedList, snapshot.date, numberOfEvents);
    this.eventCounters.set(centreCodeWithYear, 0);
    await axios.post(
      this.BASE_URL_POST + 'snapshot/save/' + `${centreCodeWithYear}/${caseType}/`,
      filteredSnapshot,
      { headers: this.userService.getHeaders() }
    );
  }

  async loadAllEvents(): Promise<SourceEventForList[]> {
    if (this.cacheExpiring > new Date() && this.cache) {
      return this.cache;
    }

    const centreCode = this.yearService.getCentreCodeWithSelectedYear().value;
    const caseType = this.currentContextService.getCaseConfiguration().caseName;
    let data = [];
    try {
      data = (
        await axios.get(this.BASE_URL_GET + `${centreCode}/${caseType}/`)
      ).data;
    } catch (e: any) {
      this.handleError(e);
    }

    const resultArray: ZsourceEvents = parseElementWithZod(ZsourceEventsSchema, ZodSchemas.SOURCE_EVENT,
      data.map((event: any) => JSON.parse(event.data)));
    this.cache = resultArray;
    this.cacheExpiring = new Date(new Date().getTime() + 1 * 60 * 1000);
    return resultArray;
  }

  async loadAllEventsForCentreCode(centreCode: string): Promise<SourceEventForList[]> {
    if (this.cacheExpiring > new Date() && this.cache) {
      return this.cache;
    }
    const caseType = this.currentContextService.getCaseConfiguration().caseName;
    let data = [];
    try {
      data = (
        await axios.get(this.BASE_URL_GET + `${centreCode}/${caseType}/`)
      ).data;
    } catch (e: any) {
      this.handleError(e);
    }
    const resultArray: SourceEventForList[] = [];
    data.forEach((event: any) => {
      const parsedEvent = JSON.parse(event.data);
      const eventCentreCode = event.centreCode; // Assuming the event has a centreCode property
      if (eventCentreCode == centreCode) {
        resultArray.push(parsedEvent);
      }
    });
    this.cache = resultArray;
    this.cacheExpiring = new Date(new Date().getTime() + 1 * 60 * 1000);
    return resultArray;
  }

  async saveEvent(event: SourceEventForList): Promise<void> {
    //TODO: better get centrecode from case, not from user
    try {
      const centreCode = this.yearService.getCentreCodeWithAdmissionYear().value;
      const caseType = this.currentContextService.getCaseConfiguration().caseName;
      await axios.post(this.BASE_URL_POST + `${centreCode}/${caseType}/`, event, { headers: this.userService.getHeaders() });
    } catch (e: any) {
      this.handleError(e);
    }
    this.cacheExpiring = new Date();
    this.cache = null;
    // todo: verify if we need to handle error
  }
}

function formatHeadersAsHTML(headers: any) {
  return Object.entries(headers)
    .map(([key, value]) => `${key}: ${value}`)
    .join('\n');
}

function formatData(data: any) {
  return Object.entries(data)
    .map(([key, value]) => `${key}: ${value}`)
    .join('\n');
}
